import type { PicqerLocation, PicqerLocationStockHistoryEntry } from './types';

/** Picqer parent locations (e.g. C, C.1, C.1.A) group child bins but are not real storage spots. */
export function getParentLocationIds(locations: PicqerLocation[]): Set<number> {
  const parentIds = new Set<number>();
  for (const loc of locations) {
    const parentId = loc.parent_idlocation;
    if (parentId != null) parentIds.add(parentId);
  }
  return parentIds;
}

export function isLeafLocation(loc: PicqerLocation, parentIds: Set<number>): boolean {
  return !parentIds.has(loc.idlocation);
}

/** Parent/grouping node with loaded history showing stock was stored here. */
export function hasStockHistory(
  idlocation: number,
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>,
  preRangeStockByLocation: Record<number, number>,
): boolean {
  const history = historyByLocation[idlocation];
  if (history === undefined) return false;
  if ((preRangeStockByLocation[idlocation] ?? 0) > 0) return true;
  return history.some((e) => e.new_stock > 0 || e.old_stock > 0 || e.stock_change !== 0);
}

/** Leaf locations, plus parent nodes that actually held stock (per loaded history). */
export function isAnalysableLocation(
  loc: PicqerLocation,
  parentIds: Set<number>,
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>,
  preRangeStockByLocation: Record<number, number>,
): boolean {
  if (!parentIds.has(loc.idlocation)) return true;
  return hasStockHistory(loc.idlocation, historyByLocation, preRangeStockByLocation);
}

/** Keep only real storage locations (no child locations in the same warehouse). */
export function filterLeafLocations(locations: PicqerLocation[]): PicqerLocation[] {
  const parentIds = getParentLocationIds(locations);
  if (parentIds.size === 0) return locations;
  return locations.filter((loc) => isLeafLocation(loc, parentIds));
}

export function filterAnalysableLocations(
  locations: PicqerLocation[],
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>,
  preRangeStockByLocation: Record<number, number>,
): PicqerLocation[] {
  const parentIds = getParentLocationIds(locations);
  return locations.filter((loc) =>
    isAnalysableLocation(loc, parentIds, historyByLocation, preRangeStockByLocation),
  );
}

export function filterAnalysableLocationsByWarehouse(
  locationsByWarehouse: Record<number, PicqerLocation[]>,
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>,
  preRangeStockByLocation: Record<number, number>,
): Record<number, PicqerLocation[]> {
  const result: Record<number, PicqerLocation[]> = {};
  for (const [idwarehouse, locations] of Object.entries(locationsByWarehouse)) {
    result[Number(idwarehouse)] = filterAnalysableLocations(
      locations,
      historyByLocation,
      preRangeStockByLocation,
    );
  }
  return result;
}

export function filterLeafLocationsByWarehouse(
  locationsByWarehouse: Record<number, PicqerLocation[]>,
): Record<number, PicqerLocation[]> {
  const result: Record<number, PicqerLocation[]> = {};
  for (const [idwarehouse, locations] of Object.entries(locationsByWarehouse)) {
    result[Number(idwarehouse)] = filterLeafLocations(locations);
  }
  return result;
}

export function countExcludedParentLocations(
  locations: PicqerLocation[],
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>,
  preRangeStockByLocation: Record<number, number>,
): number {
  const parentIds = getParentLocationIds(locations);
  return locations.filter(
    (loc) =>
      parentIds.has(loc.idlocation) &&
      !hasStockHistory(loc.idlocation, historyByLocation, preRangeStockByLocation),
  ).length;
}
